import React from 'react';
import { Tooltip } from 'antd';

export type RiskLevel = 'Low' | 'Moderate' | 'High';

export type AccountRisk = {
  score: number;
  level: RiskLevel;
};

const FILLED_SEGMENTS: Record<RiskLevel, number> = { Low: 1, Moderate: 2, High: 3 };

// The risk-scores API returns only a 0-100 score; these bands are the
// product's Low / Moderate / High cut-offs used by both portals.
export const riskLevelFromScore = (score: number): RiskLevel => {
  if (score <= 33) {
    return 'Low';
  }
  if (score <= 66) {
    return 'Moderate';
  }
  return 'High';
};

export const accountRiskFromScore = (score: number | null | undefined): AccountRisk | null =>
  score == null || !Number.isFinite(score) ? null : { score, level: riskLevelFromScore(score) };

export const RiskScorePill: React.FC<{ risk?: AccountRisk | null }> = ({ risk }) => {
  if (!risk) {
    return <span className="risk-score-empty">—</span>;
  }

  const filled = FILLED_SEGMENTS[risk.level];
  return (
    <Tooltip title={`Risk score ${risk.score} of 100`} trigger={['hover', 'focus']}>
      <span className="risk-score" tabIndex={0}>
        <span className={`risk-score-pill risk-score-pill--${risk.level.toLowerCase()}`}>
          <span className="risk-score-meter" aria-hidden>
            {[0, 1, 2].map((segment) => (
              <span key={segment} className={segment < filled ? 'is-filled' : undefined} />
            ))}
          </span>
          <span className="risk-score-level">{risk.level}</span>
        </span>
        <span className="risk-score-value">{risk.score}</span>
      </span>
    </Tooltip>
  );
};

export default RiskScorePill;
